import React from 'react';
import * as icons from 'react-icons/fa';
import data from '../../data/tools.json';
import { motion } from 'framer-motion';

const BottomSection = ({ toolId }) => {
  const toolData = data.tools[toolId] ;
  if (!toolData || !toolData.bottomSection) {
    console.error(`Tool data or bottomSection not found for toolId: ${toolId}`);
    return null;
  }
  const { bottomSection } = toolData;

  const getIconComponent = (iconName) => {
    const iconKey = data.commonIcons[iconName] || 'FaQuestionCircle';
    return icons[iconKey] || icons.FaQuestionCircle;
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren"
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  const cardHoverVariants = {
    hover: {
      y: -5,
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.section 
      className="bg-gradient-to-b from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 pt-16 mt-8 rounded-xl shadow-lg relative overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
    >
      <div className="container mx-auto px-4">
        {bottomSection && (
          <>
            {/* Features Section */}
            {bottomSection.features && (
              <motion.div className="mb-16" variants={containerVariants}>
                <motion.h2 
                  className="text-3xl md:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
                  variants={itemVariants}
                >
                  {bottomSection.title}
                </motion.h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {bottomSection.features.map((feature, index) => {
                    const IconComponent = getIconComponent(feature.icon);
                    return (
                      <motion.div 
                        key={index}
                        className="group bg-white dark:bg-gray-800 p-8 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 transition-all duration-300 hover:border-blue-100 dark:hover:border-blue-800 relative overflow-hidden"
                        variants={{
                          ...itemVariants,
                          hover: cardHoverVariants.hover
                        }}
                        whileHover="hover"
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
                        <div className="text-blue-500 text-5xl mb-6 group-hover:text-blue-600 transition-colors duration-300">
                          <IconComponent />
                        </div>
                        <h3 className="text-xl font-semibold mb-3 text-gray-800 dark:text-gray-100 group-hover:text-gray-900 dark:group-hover:text-white transition-colors duration-300">
                          {feature.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors duration-300">
                          {feature.description}
                        </p>
                        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-green-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* About Section */}
            {bottomSection.about && (
              <motion.div 
                className="mb-16 bg-white dark:bg-gray-800 p-8 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
                variants={itemVariants}
              >
                <motion.h2 
                  className="text-2xl md:text-3xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"
                  whileInView={{ x: [-20, 0], opacity: [0, 1] }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  {bottomSection.about.title}
                </motion.h2>
                <div className="prose max-w-none">
                  {bottomSection.about.description.map((paragraph, index) => (
                    <motion.p 
                      key={index}
                      className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed"
                      whileInView={{ opacity: [0, 1], y: [10, 0] }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                  {bottomSection.about.useCases && (
                    <>
                      <motion.h3 
                        className="text-xl font-semibold mt-8 mb-4 text-gray-800 dark:text-gray-100"
                        whileInView={{ x: [-10, 0], opacity: [0, 1] }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                      >
                        Common Use Cases
                      </motion.h3>
                      <ul className="space-y-2">
                        {bottomSection.about.useCases.map((useCase, index) => (
                          <motion.li 
                            key={index}
                            className="flex items-start text-gray-600 dark:text-gray-300"
                            whileInView={{ opacity: [0, 1], x: [-10, 0] }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.3, delay: 0.4 + index * 0.05 }}
                          >
                            <span className="text-blue-500 mr-2 mt-1">•</span>
                            <span className="hover:text-gray-800 dark:hover:text-gray-100 transition-colors duration-200">
                              {useCase}
                            </span>
                          </motion.li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </motion.div>
            )}

            {/* Decorative elements */}
            <motion.div 
              className="absolute bottom-0 right-0 w-40 h-40 bg-blue-100 rounded-full opacity-10 -z-10"
              animate={{
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut"
              }}
            />
          </>
        )}
      </div>
    </motion.section>
  );
};

export default BottomSection;